import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import React, { useCallback, useMemo, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableStateContext } from '../../../../contexts/tableState/TableStateContext';
import { ETablePopups } from '../../../../types/enums/ETablePopups';
import { ETables } from '../../../../types/enums/ETables';
import { IEditForm } from '../../../../types/interfaces/IEditForm';
import { endpoints } from '../../../../utils/endpoints';
import tPaths from '../../../../utils/tPaths';
import useApi from '../../../../utils/useApi';
import CustomSelect from '../../../CustomSelect/CustomSelect';
import InputForm from '../../../InputForm/InputForm';
import SwitchInput from '../../../SwitchInput/SwitchInput';
import useStyles from './GenerateCodesDialog.styles';

enum EFields {
  AMOUNT = 'GenerateAmount',
  GROUP = 'SelectGroup',
  SHOW_IN_ADMIN_PANEL = 'ShowInAdminPanel',
}

interface IProps {
  tableName: string;
  isOpen: boolean;
  onHandleGenerate: (amount: string, groupName: string, showInAdminPanel: boolean) => void;
}

const InitState: Record<EFields, string | boolean> = {
  [EFields.AMOUNT]: '',
  [EFields.GROUP]: '',
  [EFields.SHOW_IN_ADMIN_PANEL]: true,
};

const GenerateCodesDialog: React.FC<IProps> = ({
  tableName,
  isOpen,
  onHandleGenerate,
  // handleOpenGenerateCodesPopup,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { get } = useApi(null);
  const { setPopup } = useContext(TableStateContext);
  const [data, setData] = useState<string[]>([]);
  const [state, setState] = useState<Record<EFields, string | boolean>>(InitState);

  const onChange = useCallback((fieldName: string, value: string) => {
    setState((s) => ({ ...s, [fieldName]: value }));
  }, []);

  const fields: Record<EFields, IEditForm> = useMemo(
    () => ({
      [EFields.AMOUNT]: {
        fieldName: EFields.AMOUNT,
        placeholder: '100',
        value: state[EFields.AMOUNT],
        onChange,
        type: 'input',
        fullWidth: true,
        label: <Typography className={classes.inputLabel}>{t(tPaths.tableGenerateCodes.howMany)}</Typography>,
      },
      [EFields.GROUP]: {
        fieldName: EFields.GROUP,
        onChange,
        label: <Typography className={classes.selectLabel}>{t(tPaths.tableGenerateCodes.selectGroup)}</Typography>,
        value: state[EFields.GROUP],
        intialValue: data[0],
        fullWidth: true,
        type: 'select',
        variants: data.length
          ? [
              ...data.map((value: string) => ({
                value,
                label: value,
              })),
            ]
          : [],
      },
      [EFields.SHOW_IN_ADMIN_PANEL]: {
        fieldName: EFields.SHOW_IN_ADMIN_PANEL,
        type: 'switch',
        onChange,
        label: <Typography className={classes.selectLabel}>{t(tPaths.tableGenerateCodes.showInAdminPanel)}</Typography>,
        value: state[EFields.SHOW_IN_ADMIN_PANEL],
      },
    }),
    [classes.inputLabel, classes.selectLabel, data, onChange, state, t],
  );

  const closePopup = useCallback(() => {
    setPopup(tableName, ETablePopups.GeneratePromocodes, { isOpen: false });
  }, [setPopup, tableName]);

  useEffect(() => {
    if (tableName === ETables.PromoCodes && !data.length) {
      get({
        endpoint: endpoints.promocodesGroups(tableName),
        onSuccess: ({ data }) => {
          setState((s) => ({ ...s, [EFields.GROUP]: data[0] }));
          setData(data);
        },
      });
    }
  }, [get, tableName, isOpen]);

  return (
    <Dialog open={isOpen} fullWidth={true} onBackdropClick={closePopup}>
      <DialogTitle>
        <Typography className={classes.title}>{t(tPaths.tableGenerateCodes.title)}</Typography>
      </DialogTitle>

      <DialogContent>
        <Box pt={1}>
          <InputForm {...fields[EFields.AMOUNT]} />
        </Box>
        <Box pt={5}>
          <CustomSelect {...fields[EFields.GROUP]} />
        </Box>
        <Box pt={5}>
          <SwitchInput {...fields[EFields.SHOW_IN_ADMIN_PANEL]} />
        </Box>
      </DialogContent>
      <DialogActions className={classes.actionsContainer}>
        <Button className={classes.button} onClick={closePopup}>
          {t(tPaths.global.cancel)}
        </Button>
        <Button
          className={classes.button}
          color="secondary"
          onClick={() =>
            onHandleGenerate(
              state[EFields.AMOUNT].toString(),
              state[EFields.GROUP].toString(),
              !!state[EFields.SHOW_IN_ADMIN_PANEL],
            )
          }
        >
          {t(tPaths.tableGenerateCodes.generateButton)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GenerateCodesDialog;
